import { HttpErrorResponse } from '@angular/common/http';

/** Fixed codes the API returns (ValidationException / KeyNotFound / Unauthorized), as readable text. */
const API_ERROR_TEXT: Record<string, string> = {
  // Investigations
  InvestigationClosedCannotEdit: 'This investigation is closed and can no longer be edited.',
  InvestigationNotClosed: 'This investigation is already open.',
  InvestigationRequired: 'The investigation is required.',
  UserNotSystemAdministration: 'Only System Administration can reopen investigations.',
  UserDoesNotHaveCreatePermissionForInvestigations:
    'You do not have permission to create investigations.',
  // Incidents
  incidentRequired: 'The incident is required.',
  incidentFullyCompleted: 'This incident is closed and can no longer be edited.',
  PersonNotEditable: 'Only Manual Entry records can be edited.',
  stageARequiredForStageB: 'Stage A must be completed first.',
  stageBRequiredForStageC: 'Stage B must be completed first.',
  stageCRequiredForStageD: 'Stage C must be completed first.',
  stageBRequiredForStageD: 'Stage B must be completed first.',
  programIdRequired: 'Select a program.',
  programLocationNotBelongToProgram: 'The selected location does not belong to the program.',
  piInvolvementsNotAllowedForNoClientsSelection:
    'Remove the clients involved, or change the clients selection.',
  piInvolvementRequiredForSingleClientsSelection: 'Add the client involved.',
  piInvolvementRequiredForMultipleClientsSelection: 'Add the clients involved.',
  piInvolvementTypeIdRequiredForMultipleClientsSelection:
    'Every client involved needs an involvement type.',
  OnlyOneInvolvementIsAllowed: 'Only one client can be involved with this selection.',
  MultipleInvolvementIsRequired: 'Add at least two clients involved.',
  // Programs and data elements
  ProgramRequired: 'Select a program.',
  PersonRequired: 'Select a person.',
  CDEProgramRequired: 'Add at least one data element.',
  CDEProgramAlreadyAssigned: 'This data element is already assigned to the program.',
  DuplicateProgramId: 'A program was added more than once.',
  // Persons (JCCA-173: duplicates are checked by CIN only)
  CinExist: 'This CIN is already in use. Check if this record already exists.',
  // Search, documents, configuration
  DateFromAndDateToRequired: 'Choose a start and an end date.',
  OnlyIncidentIdOrInvestigationId: 'A document must belong to an incident or an investigation.',
  OnlyOnePrametterMustBeOne: 'Choose only one: clients, staff or others.',
  // Generic
  Required: 'A required field is missing.',
  Forbidden: 'You do not have permission to do this.',
  InvalidFormatId: 'The record identifier is not valid.',
  InvalidFormatDate: 'The date is not valid.',
  UserNotAuthenticated: 'Your session has expired. Sign in again.',
  UserWithoutPermissions: 'You do not have permission to do this.',
};

/** Entity class names (as the API reports them) shown to users. */
const ENTITY_LABEL: Record<string, string> = {
  ConfiguredDataElement: 'data element',
  CDEProgram: 'program assignment',
  CDEListItem: 'list item',
  CDEDataType: 'data type',
  SituationCategory: 'category',
  ResponseCategory: 'category',
  SituationTaxonomy: 'taxonomy',
  ResponseTaxonomy: 'taxonomy',
  SituationTaxonomyMember: 'category',
  ResponseTaxonomyMember: 'category',
  RollupSituationCategory: 'rollup category',
  RollupResponseCategory: 'rollup category',
  RollupTaxonomy: 'rollup taxonomy',
  ProgramLocation: 'location',
  ReportRecipient: 'report recipient',
  DocumentType: 'document type',
  PIInvolvementType: 'involvement type',
  SubstantiveArea: 'substantive area',
};

/** "PhoneNumber" / "Phone_Number" → "phone number". */
function humanize(name: string): string {
  return name
    .replace(/_/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
    .trim()
    .toLowerCase();
}

function entityLabel(name: string): string {
  return ENTITY_LABEL[name] ?? humanize(name);
}

function capitalize(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1);
}

/**
 * Readable text for one API error entry, or null when it is an unknown code
 * (a bare identifier is not useful to users; the caller's fallback is better).
 */
function describe(entry: string): string | null {
  const text = entry.trim().replace(/\.$/, '');
  if (!text || text === 'Validation failed' || text === 'ValidationFailed') return null;
  // HTML / XML bodies (proxies, Azure Storage) are not user messages.
  if (text.startsWith('<')) return null;
  if (API_ERROR_TEXT[text]) return API_ERROR_TEXT[text];

  // FK violation on delete (Auditable.SaveChangesAsync, SQL 547).
  const inUse = /^(\w+)HasAssociatedRecords$/.exec(text);
  if (inUse) {
    return `This ${entityLabel(inUse[1])} cannot be deleted because it is currently in use.`;
  }

  const duplicate = /^DuplicateException(\w*)$/.exec(text);
  if (duplicate) {
    return duplicate[1]
      ? `A record with this ${humanize(duplicate[1])} already exists.`
      : 'This record already exists.';
  }

  const notFound = /^(\w+?)NotFound(?:_.*)?$/.exec(text);
  if (notFound) return `The ${entityLabel(notFound[1])} no longer exists. Reload and try again.`;

  if (text.startsWith('ReferencedNotFound_')) {
    return 'A related record no longer exists. Reload and try again.';
  }

  // Raw SQL error that slipped through (value longer than the column).
  const truncated = /would be truncated in table '[^']*', column '([^']+)'/.exec(text);
  if (truncated) return `${capitalize(humanize(truncated[1]))} is too long.`;

  // Sentences (DataAnnotations messages, e.g. "Sex cannot exceed 1 character") are already readable.
  if (/\s/.test(text)) return entry.trim();
  return null;
}

function join(entries: unknown[]): string | null {
  const messages = entries
    .filter((e): e is string => typeof e === 'string')
    // ValidationException messages can hold several codes joined by '*'.
    .flatMap((e) => (e.includes('*') && !/\s/.test(e) ? e.split('*') : [e]))
    .map(describe)
    .filter((m): m is string => !!m);
  return messages.length ? [...new Set(messages)].join(' ') : null;
}

/** True when the API error carries `code` (e.g. to flag the field it refers to). */
export function apiErrorHasCode(err: unknown, code: string): boolean {
  if (!(err instanceof HttpErrorResponse) || err.error == null) return false;
  const body = typeof err.error === 'string' ? err.error : JSON.stringify(err.error);
  return new RegExp(`\\b${code}\\b`).test(body);
}

/**
 * Human-readable message from a jcca-api error response, or `fallback`.
 *
 * The API answers errors in several shapes:
 * - ValidationException in a controller → `{ message, errors: string[] }`
 * - ASP.NET model validation ([Required], [StringLength]) → `{ title, errors: { field: string[] } }`
 * - Global exception middleware → `{ error, statusCode }`
 * - NotFound / Forbidden / Unauthorized → `{ message }`, `{ message, details }`, `{ message, detail }`
 * - Unified search → `{ message, errors: string }`; some endpoints a plain string ("Code1*Code2")
 *
 * Known codes become readable text; unknown bare codes fall back to `fallback`,
 * which stays the message for unexpected (500 / network) errors.
 */
export function apiErrorMessage(err: unknown, fallback: string): string {
  if (!(err instanceof HttpErrorResponse)) return fallback;
  if (err.status === 0) return 'Could not reach the server. Check your connection and try again.';
  // 5xx bodies carry internal details ("Internal server error: …"), not user messages.
  if (err.status >= 500) return fallback;

  const body = err.error;
  let message: string | null = null;

  if (typeof body === 'string') {
    message = join([body]);
  } else if (body && typeof body === 'object') {
    if (Array.isArray(body.errors)) {
      message = join(body.errors);
    } else if (typeof body.errors === 'string') {
      message = join([body.errors]);
    } else if (body.errors && typeof body.errors === 'object') {
      message = join(Object.values(body.errors as Record<string, unknown>).flat());
    }
    message ??= join([body.details, body.detail, body.error, body.message]);
  }

  if (message) return message;
  if (err.status === 403) return 'You do not have permission to do this.';
  if (err.status === 401) return 'Your session has expired. Sign in again.';
  return fallback;
}
